import React, { useState, useRef, useEffect } from 'react';
import { VSA_Spec } from '../../../api_types';
import backend from '../../../backend';
import { VideoSegment, VideoAnnotation, ActionAnnotation } from './types';
import VideoTimelineSlider from './VideoTimelineSlider';
import SegmentEditor from './SegmentEditor';
import BusyOverlay from './BusyOverlay';
import PlayPauseButton from './PlayPauseButton';
import SpeedControl from './SpeedControl';
import ExerciseVideo from './ExerciseVideo';
import { InfoPanel } from './InfoPanel';


const SPEED_OPTIONS = [0.5, 1, 2, 4, 8];
const STARTING_SPEED = 4;

const VideoSegmentAnnotator: React.FC = () => {
    const [data, setData] = useState<VSA_Spec|null>(null);
    const [url, setUrl] = useState<string>("");
    // const [coachVideoUrl, setCoachVideoUrl] = useState<string>("");
    const [videoSpeed, setVideoSpeedState] = useState<number>(STARTING_SPEED);
    const [videoAnnotation, setVideoAnnotation] = useState<VideoAnnotation>(new VideoAnnotation());
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [videoDuration, setVideoDuration] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const setSpeed = (x: number) => {
         setVideoSpeedState(x);
         if (videoRef.current) {
             videoRef.current.playbackRate = x;
         }
    };



    const load = async () => {
        setIsLoading(true);
        try {
            const data: VSA_Spec | null = await backend.vsa_todo();
            console.log("====================================");
            console.log("load:", data);
            console.log("====================================");
            if (data) {
                const url = "https://annotator.kemtai.com/rvvvv/" + data.data.url;
                setData(data);
                setUrl(url);

                // Apply current speed to new video
                setTimeout(() => setSpeed(videoSpeed), 100);
            }
        } catch (error) {
            console.error("Failed to load video segment data", error);
        } finally {
            setIsLoading(false);
        }
    }

    // Video segment annotation methods
    const addNewSegment = () => {
        setVideoAnnotation(prev => {
            const newAnnotation = prev.splitAt(currentTime);
            return newAnnotation;
        });
    };

    const updateSegmentAnnotation = (segmentIndex: number, annotation: ActionAnnotation) => {
        setVideoAnnotation(prev => {
            const newAnnotation = prev.updateSegmentAnnotation(segmentIndex, annotation);
            return newAnnotation;
        });
    };

    const deleteSegment = (segmentIndex: number) => {
        setVideoAnnotation(prev => {
            const newAnnotation = prev.deleteSegment(segmentIndex);
            return newAnnotation;
        });
    };

    const handleTimeChange = (newTime: number) => {
        setCurrentTime(newTime);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime;
        }
    };

    const handleSliderMouseDown = () => {
        setIsDragging(true);
        pauseVideo();
    };

    const handleSliderMouseUp = () => {
        setIsDragging(false);
        pauseVideo();
    };

    const formatTimeDisplay = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const togglePlayPause = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    };

    const pauseVideo = () => {
        if (videoRef.current) {
            if (!videoRef.current.paused) {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    };



    const handleSegmentClick = (segment: VideoSegment) => {
        if (videoRef.current) {
            videoRef.current.currentTime = segment.beginTime;
            setCurrentTime(segment.beginTime);
        }
    };

    const convertToVideoSegments = (annotation: VideoAnnotation): VideoSegment[] => {
        return annotation.segments.map(segment => ({
            id: segment.index.toString(),
            beginTime: segment.beginTime,
            endTime: segment.endTime,
            text: `${segment.annotation.action}${segment.annotation.comment ? ': ' + segment.annotation.comment : ''}`
        }));
    };

    const save = async () => {
        if (!data) {
            console.error("No data to save");
            return;
        }
        setIsSaving(true);
        try {
            await backend.vsa_save(data.tid, videoAnnotation.json());
            await load();
        } catch (error) {
            console.error("Failed to save video segment data", error);
        } finally {
            setIsSaving(false);
        }
    };


    useEffect(() => {
        load();
    }, []);

    // Update current time periodically
    useEffect(() => {
        const interval = setInterval(() => {
            if (videoRef.current && !isDragging) {
                setCurrentTime(videoRef.current.currentTime);
            }
        }, 100);
        return () => clearInterval(interval);
    }, [isDragging]);

    // Set up video event listeners
    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            console.log("videoRef is null");
            return undefined;
        }

        const handleDurationChange = () => {
            const video = videoRef.current;
            const duration = video!.duration || 0;
            setVideoDuration(duration);
            setVideoAnnotation(new VideoAnnotation(duration));
            // if (video && duration>0) {
            //        video.currentTime = 0;
            // }
        };
        const onLoadedMetadata = () => {
            const video = videoRef.current;
            // console.log("onLoadedMetadata>> ", video?.duration);
            // if (video){
            //    video.currentTime = 1e10;
            // }

            handleDurationChange();
        }
        const onLoadedData = () => {
            const video = videoRef.current;
            console.log("onLoadedData>> ", video?.duration);
            handleDurationChange();
        }

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => setIsPlaying(false);

        video.addEventListener('loadedmetadata', onLoadedMetadata);
        video.addEventListener('loadeddata', onLoadedData);
        video.addEventListener('durationchange', handleDurationChange);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);

        // Inline call ensures duration state updates even if metadata is already available.
        handleDurationChange();

        return () => {
            video.removeEventListener('loadedmetadata', onLoadedMetadata);
            video.removeEventListener('loadeddata', onLoadedData);
            video.removeEventListener('durationchange', handleDurationChange);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
        };
    }, [url]);

    // Set up keyboard event listeners
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.code === 'Space' &&
                event.target instanceof Element &&
                event.target.tagName !== 'INPUT' &&
                event.target.tagName !== 'TEXTAREA') {
                event.preventDefault();
                const video = videoRef.current;
                if (video) {
                    if (video.paused) {
                        video.play();
                        setIsPlaying(true);
                    } else {
                        video.pause();
                        setIsPlaying(false);
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const isBusy = isLoading || isSaving;

    return (
        <div style={{ position: 'relative' }}>
            <InfoPanel stats={data?.stats} />
        
            <BusyOverlay isLoading={isLoading} isSaving={isSaving} />
            <video 
                ref={videoRef} 
                src={url} 
                autoPlay 
                loop
                muted
                width="640px" 
                height="480px"
            />
            
            {/* Custom Video Timeline Slider */}
            <VideoTimelineSlider
                currentTime={currentTime}
                videoDuration={videoDuration}
                onTimeChange={handleTimeChange}
                onMouseDown={handleSliderMouseDown}
                onMouseUp={handleSliderMouseUp}
                formatTimeDisplay={formatTimeDisplay}
                segments={convertToVideoSegments(videoAnnotation)}
                onSegmentClick={handleSegmentClick}
                videoAnnotationSegments={videoAnnotation.segments}
            />

            {/* <video 
                className='coach-video' 
                src={coachVideoUrl} 
                autoPlay 
            /> */}
            
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '15px', 
                margin: '15px 0',
                padding: '10px',
                width: '820px',
            }}>
                <PlayPauseButton 
                    isPlaying={isPlaying} 
                    onToggle={togglePlayPause}
                />

                    
                    <button 
                        type="button"
                        onClick={addNewSegment}
                        disabled={isBusy}
                        style={{ 
                            marginLeft: '20px', 
                            backgroundColor: '#4CAF50', 
                            color: 'white', 
                            border: 'none', 
                            padding: '8px 15px',
                            borderRadius: '3px',
                            cursor: isBusy ? 'not-allowed' : 'pointer',
                            opacity: isBusy ? 0.6 : 1,
                            transition: 'opacity 0.2s ease'
                        }}
                    >
                        <span role="img" aria-label="split" style={{ marginRight: '6px' }}>✄</span>
                       Split  {/*  at Current Time */}
                    </button>


                    <button 
                        type="button"
                        onClick={save}
                        disabled={isBusy}
                        style={{ 
                            marginLeft: '20px', 
                            backgroundColor: '#4CAF50', 
                            color: 'white', 
                            border: 'none', 
                            padding: '8px 15px',
                            borderRadius: '3px',
                            cursor: isBusy ? 'not-allowed' : 'pointer',
                            opacity: isBusy ? 0.6 : 1,
                            transition: 'opacity 0.2s ease'
                        }}
                    >
                        <span role="img" aria-label="save" style={{ marginRight: '6px' }}>{isSaving ? '⏳' : '🖫'}</span>
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>


                <SpeedControl 
                    speedOptions={SPEED_OPTIONS}
                    currentSpeed={videoSpeed}
                    onSpeedChange={setSpeed}
                />

            </div>

            <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
                

                {videoAnnotation.segments.length === 0 ? (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>
                        No segments available. Initialize video to start annotating.
                    </p>
                ) : (
                    <div>
                        {/* <h4>Segments ({videoAnnotation.segments.length}):</h4> */}
                        {videoAnnotation.segments.map(segment => (
                            <SegmentEditor
                                key={segment.index}
                                segment={segment}
                                onUpdate={updateSegmentAnnotation}
                                onDelete={deleteSegment}
                                onSetCurrentTime={handleTimeChange}
                                currentTime={currentTime}
                            />
                        ))}
                    </div>
                )}
            </div>    
            <p>File: {data?.data.name} {data?.data.exid} {data?.data.uuid}</p>       
            {data && <ExerciseVideo uuid={data?.data.uuid} />}
        </div>
        
    );
};

export default VideoSegmentAnnotator;
